import type { BlueprintMessage } from "@/types/creativesphere";
import type { ProductCardsTaxonomyFilter } from "@/types/ingestedProduct";
import type { ProductGroupType } from "@/types/propagation";
import type { Dispatch, SetStateAction } from "react";

import { createStepStateContext } from "@/features/Generations/context/createStepStateContext";

export type PilotOrigin = "match" | "blueprint";

export interface SelectedProduct {
  id: string;
  title: string;
  image_url: string;
  description?: string;
  category?: string;
  /** Set once the catalog lookup has settled, with or without copy. */
  details_loaded?: boolean;
}

export interface SelectedProductGroup {
  group_type: ProductGroupType;
  /** Absent on an All Products launch, which is scoped by no taxonomy handle. */
  group_id?: string;
  handle?: string;
}

export interface PilotState {
  productId?: string;
  origin?: PilotOrigin;
  product?: SelectedProduct;
  productGroup?: SelectedProductGroup;
  blueprint?: BlueprintMessage;
  // Slug of the Propagation created when the pilot is kicked off (Step 2's
  // "Create pilot"). Carries the workflow forward to the accept-pilot step.
  propagationSlug?: string;
  // Slug of the pilot Generation (POST /generations), set alongside
  // propagationSlug on "Run generation"; polled in Step 2 to render the image.
  generationSlug?: string;
  taxonomyFilter?: ProductCardsTaxonomyFilter;
  pilotImageUrl?: string;
  // The product or blueprint changed after the propagation was created, so
  // the stored pilot no longer matches; the next run updates it in place.
  pilotInputsChanged?: boolean;
  // Stored pilot of a reopened story when there is no generation to poll:
  // still rendering, or failed and awaiting a re-run.
  pilotRunState?: "generating" | "failed";
}

export interface PilotStateValue {
  pilotState: PilotState;
  setPilotState: Dispatch<SetStateAction<PilotState>>;
}

const { Provider, useStepState } = createStepStateContext<PilotState>("Pilot");

export { Provider as PilotStateProvider };

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with the provider it reads
export function usePilotState(): PilotStateValue {
  const { state, setState } = useStepState();
  return { pilotState: state, setPilotState: setState };
}
